import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ActionIcon, Box, Button, Flex, Group, Table, Text, Tooltip } from "@mantine/core";
import { Calendar } from "@mantine/dates";
import { IconEdit } from "@tabler/icons-react";

import { Loader } from "components";
import { DateUtils } from "utils";

interface UserSimple {
  id: number;
  fullName: string;
}

export interface AbsenceResponse {
  id: number;
  agent: UserSimple;
  replacementAgent?: UserSimple;
  dateFrom: string;
  dateTo: string;
  workingDays: number;
  reason?: string;
  note?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELED';
  createdOn?: string;
  createdBy?: UserSimple;
}

export interface HolidayResponse {
  id: number;
  name: string;
  date: string;
}

interface AbsencesTabProps {
  absences: AbsenceResponse[];
  holidays?: HolidayResponse[];
  onRequestClick: () => void;
  onAbsenceClick: (id: number) => void;
  onStatusEditClick?: (absence: AbsenceResponse) => void;
  loading?: boolean;
  readOnly?: boolean;
  isAdmin?: boolean;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "APPROVED":
      return "green";
    case "REJECTED":
      return "red";
    case "PENDING":
      return "violet";
    case "CANCELED":
      return "gray";
    default:
      return "gray";
  }
};

const formatDate = (dateString: string) => {
  return DateUtils.formatDateDisplay(dateString);
};

const AbsencesTab = ({ absences, holidays = [], onRequestClick, onAbsenceClick, onStatusEditClick, loading, readOnly, isAdmin }: AbsencesTabProps) => {
  const { t } = useTranslation();

  // Create a map for quick holiday lookup
  const holidayMap = useMemo(() => {
    const map = new Map<string, string>();
    holidays.forEach((h) => {
      map.set(h.date, h.name);
    });
    return map;
  }, [holidays]);

  return (
    <Flex gap={60}>
      {/* Tabela odsustva */}
      <Box style={{ flex: 0.8 }}>
        <Table verticalSpacing="xs" horizontalSpacing="sm" striped stripedColor="gray.0" bg="gray.2" highlightOnHover highlightOnHoverColor="gray.1">
          <Table.Thead bg="white">
            <Table.Tr>
              <Table.Th>{t("profilePage.absences.table.createdOn")}</Table.Th>
              <Table.Th>{t("profilePage.absences.table.dateFrom")}</Table.Th>
              <Table.Th>{t("profilePage.absences.table.dateTo")}</Table.Th>
              <Table.Th>{t("profilePage.absences.table.workingDays")}</Table.Th>
              <Table.Th>{t("profilePage.absences.table.replacement")}</Table.Th>
              <Table.Th>{t("profilePage.absences.table.status")}</Table.Th>
              <Table.Th>{t("profilePage.absences.table.reason")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Box py="xl">
                    <Loader />
                  </Box>
                </Table.Td>
              </Table.Tr>
            ) : absences.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="md">{t("profilePage.absences.table.empty")}</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              absences.map((absence) => (
                <Table.Tr 
                  key={absence.id} 
                  onClick={readOnly ? undefined : () => onAbsenceClick(absence.id)} 
                  style={{ cursor: readOnly ? 'default' : 'pointer' }}
                >
                  <Table.Td>{absence.createdOn ? formatDate(absence.createdOn) : '-'}</Table.Td>
                  <Table.Td>{formatDate(absence.dateFrom)}</Table.Td>
                  <Table.Td>{formatDate(absence.dateTo)}</Table.Td>
                  <Table.Td>{absence.workingDays}</Table.Td>
                  <Table.Td>{absence.replacementAgent?.fullName || '-'}</Table.Td>
<Table.Td>
                  <Group gap="xs">
                    <Text c={getStatusColor(absence.status)} fw={500}>
                      {t(`profilePage.absences.status.${absence.status}`, { defaultValue: absence.status })}
                    </Text>
                    {isAdmin && absence.status === 'PENDING' && onStatusEditClick && (
                      <ActionIcon 
                        variant="transparent" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          onStatusEditClick(absence);
                        }}
                      >
                        <IconEdit size={16} color="var(--mantine-color-gray-6)" />
                      </ActionIcon>
                    )}
                  </Group>
                </Table.Td>
                  <Table.Td>
                    {absence.reason
                      ? t(`profilePage.absences.reason.${absence.reason}`, { defaultValue: absence.reason })
                      : "-"}
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
        {!readOnly && (
          <Flex justify="flex-end" mt="md">
            <Button color="gray" radius="xl" onClick={onRequestClick}>
              {t("profilePage.absences.actions.submitRequest")}
            </Button>
          </Flex>
        )}
      </Box>

      {/* Kalendar */}
      <Box p="md" style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8 }}>
        <Calendar
          getDayProps={(date) => {
            const dateString = DateUtils.formatDateLocal(date);
            const holidayName = holidayMap.get(dateString);
            
            if (holidayName) {
              return {
                style: {
                  backgroundColor: 'var(--mantine-color-violet-6)',
                  color: 'white',
                  borderRadius: 4,
                },
              };
            }
            return {};
          }}
          renderDay={(date) => {
            const dateString = DateUtils.formatDateLocal(date);
            const holidayName = holidayMap.get(dateString);
            
            if (holidayName) {
              return (
                <Tooltip 
                  label={holidayName} 
                  withArrow
                  color="white"
                  styles={{
                    tooltip: {
                      color: 'var(--mantine-color-violet-6)',
                      fontWeight: 500,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <div>{date.getDate()}</div>
                </Tooltip>
              );
            }
            return date.getDate();
          }}
        />
      </Box>
    </Flex>
  );
};

export default AbsencesTab;
